<template>
  <form novalidate @submit.prevent="submit">
    <c-box data-cy="listorders_container">
      <c-box :mx="{ md: '4xl' }" :pt="{ base: 'md', md: 'xl' }">
        <c-heading as="h3" font-size="md" font-weight="bold" mb="md" color="gray.700">
          {{ $t("productReturnV2.selectOrder.subtitle") }}
        </c-heading>
        <p
          v-html="$t('productReturnV2.selectOrder.infoText')"
          v-chakra
          :mb="{ base: 'md', md: '3xl' }"
          color="gray.700"
        />
      </c-box>
      <c-box
        :box-shadow="{ md: 'base' }"
        :border-radius="{ md: 'base' }"
        bg="white"
        :px="{ base: 'md', md: '4xl' }"
        :pt="{ base: 'xl', md: '2.5rem' }"
        :pb="{ base: '2xl', md: '3rem' }"
        :mx="{ base: '-15px', md: '0' }"
      >
        <c-grid
          :d="{ base: 'none', md: 'grid' }"
          template-columns="1fr 1fr 1fr 2fr 1fr 2fr"
          font-weight="bold"
          pb="md"
          border-bottom="1px solid"
          border-bottom-color="gray.300"
        >
          <c-text>{{ $t("common.selection") }}</c-text>
          <c-text>{{ $t("ordering.timestamp") }}</c-text>
          <c-text>{{ $t("ordering.orderId") }}</c-text>
          <c-text>
            {{ $t("ordering.deliveryDetails.customerReference") }}
          </c-text>
          <c-text>
            {{ $t("ordering.projectDetails.projectId") }}
          </c-text>
          <c-text>
            {{ $t("shoppingCart.heading.totalPrice") }}
          </c-text>
        </c-grid>

        <c-box
          v-for="order in getOrdersApplicableForSalesReturnFilteredByReturns()"
          :key="order.orderNumber"
          data-cy="order_row_container"
          @click="setRelatedOrder(order)"
          @keydown.enter="setRelatedOrder(order)"
          tabindex="0"
          :mx="{ base: '-15px', md: '0' }"
          border-bottom="1px solid"
          border-bottom-color="gray.300"
          v-chakra="{
            'input[type=radio]:checked+div': {
              background: 'brand.blue',
              borderColor: 'brand.blue',
            },
          }"
        >
          <c-grid
            :template-columns="{ base: '1fr 7fr', md: '1fr 1fr 1fr 2fr 1fr 2fr' }"
            :bg="
              form.productDetails.orderNumber === order.orderNumber ? 'brand.blueLightest' : 'white'
            "
            py="lg"
            :px="{ base: 'md', md: '0' }"
            width="100%"
          >
            <c-radio
              :value="order.orderNumber"
              :isChecked="form.productDetails.orderNumber === order.orderNumber"
              :ml="{ md: 'md' }"
            />
            <c-box :d="{ md: 'contents' }">
              <c-flex>
                <c-text as="span" :d="{ md: 'none' }" font-weight="bold">
                  {{ $t("ordering.timestamp") }}
                </c-text>
                <c-text
                  as="span"
                  :ml="{ base: 'auto', md: '0' }"
                  :text-align="{ base: 'right', md: 'left' }"
                >
                  {{ order.timestamp | moment("D.M.YYYY") }}
                </c-text>
              </c-flex>
              <c-flex>
                <c-text as="span" :d="{ md: 'none' }" font-weight="bold">
                  {{ $t("ordering.orderId") }}
                </c-text>
                <c-text
                  as="span"
                  :ml="{ base: 'auto', md: '0' }"
                  :text-align="{ base: 'right', md: 'left' }"
                >
                  <c-text>{{ order.orderNumber }}</c-text>
                </c-text>
              </c-flex>
              <c-flex>
                <c-text as="span" :d="{ md: 'none' }" font-weight="bold">
                  {{ $t("ordering.deliveryDetails.customerReference") }}
                </c-text>
                <c-text
                  as="span"
                  :ml="{ base: 'auto', md: '0' }"
                  :text-align="{ base: 'right', md: 'left' }"
                >
                  {{ order.deliveryDetails.reference }}
                </c-text>
              </c-flex>
              <c-flex>
                <c-text as="span" :d="{ md: 'none' }" font-weight="bold">
                  {{ $t("ordering.projectDetails.projectId") }}
                </c-text>
                <c-text
                  as="span"
                  :ml="{ base: 'auto', md: '0' }"
                  :text-align="{ base: 'right', md: 'left' }"
                >
                  {{ (order.discounts && order.discounts.projectDiscountCode) || "-" }}
                </c-text>
              </c-flex>
              <c-flex :mr="{ md: 'md' }">
                <c-text as="span" :d="{ md: 'none' }" font-weight="bold">
                  {{ $t("shoppingCart.heading.totalPrice") }}
                </c-text>
                <c-text
                  as="span"
                  v-if="order.totalSum && order.totalSum.price"
                  :ml="{ base: 'auto', md: '0' }"
                  :text-align="{ base: 'right', md: 'left' }"
                >
                  <Price :price="order.totalSum.price" :currency="order.totalSum.currencyCode" />
                </c-text>
              </c-flex>
            </c-box>
          </c-grid>
        </c-box>
      </c-box>
    </c-box>
  </form>
</template>

<script lang="ts">
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import Price from "@/design-system/elements/Price.vue";
import { OrdersState } from "@/store/ordersModule";
import { IPartnerInfoState } from "@/store/partnerInfoModule";
import { filterOrders } from "@/util/OrdersApplicableForSalesReturnFilter";
import { getPhoneNumberInfo } from "@/util/PhoneNumber";
import { CBox, CFlex, CGrid, CHeading, CRadio, CText } from "@chakra-ui/vue";
import { HandlingFee, SurfaceTreatmentFee, IRootState, IReturnsState } from "@iloq/shared";
import moment from "moment-timezone";
import { defineComponent } from "vue";
import { mapState } from "vuex";

export default defineComponent({
  name: "RMA00SRSelectOrderForReturn",
  mixins: [FormValidationMixin],
  components: {
    Price,
    CBox,
    CHeading,
    CText,
    CGrid,
    CRadio,
    CFlex,
  },
  data() {
    return {
      form: {
        relatedOrderNumber: "",
        contactDetails: {
          fullName: "",
          partnerName: "",
          emailAddress: "",
          partnerAddress1: "",
          partnerAddress2: "",
          partnerAddress3: "",
          partnerZipCode: "",
          partnerCity: "",
          phoneNumber: "",
          partnerCountry: "",
        },
        productDetails: {
          timestamp: "",
          orderNumber: "",
          orderReference: "",
          productCount: 0,
          products: [],
        },
      },
    };
  },
  computed: {
    ...mapState("orders", {
      orders: (state: unknown) => (state as OrdersState).ordersApplicableForSalesReturns,
    }),
    ...mapState("returns", {
      returns: (state: unknown) => (state as IReturnsState).list,
    }),
    ...mapState({ user: (state: unknown) => (state as IRootState).user! }),
    ...mapState("partnerInfo", {
      partnerInfo: (state: unknown) => (state as IPartnerInfoState).partnerInfo,
    }),
  },
  mounted() {
    const ordersFilteredByReturns = this.getOrdersApplicableForSalesReturnFilteredByReturns();
    if (ordersFilteredByReturns.length > 0) {
      this.setRelatedOrder(ordersFilteredByReturns[0]);
    }
  },
  methods: {
    getOrdersApplicableForSalesReturnFilteredByReturns() {
      return filterOrders(this.orders, this.returns);
    },
    setRelatedOrder(order) {
      if (order) {
        if (order.orderNumber !== undefined) {
          this.form.relatedOrderNumber = order.orderNumber;
        }
        this.form.contactDetails.fullName = `${this.user.firstName} ${this.user.lastName}`;

        this.form.contactDetails.partnerAddress1 =
          order.deliveryDetails.shipToAddress.streetAddress;
        this.form.contactDetails.partnerZipCode = order.deliveryDetails.shipToAddress.zipCode;
        this.form.contactDetails.partnerCity = "";

        this.form.contactDetails.phoneNumber = this.partnerInfo.phoneNumber;
        this.form.contactDetails.phoneNumberInputValue = getPhoneNumberInfo(
          this.partnerInfo.phoneNumber || "",
          order.deliveryDetails.shipToAddress.countryCode,
        ).phoneNumberWithoutCountryCallingCode;

        this.form.contactDetails.partnerCountry = order.deliveryDetails.shipToAddress.countryCode;

        if (order.orderNumber !== undefined) {
          this.form.productDetails.orderNumber = order.orderNumber;
        }
        if (order.deliveryDetails.reference !== undefined) {
          this.form.productDetails.orderReference = order.deliveryDetails.reference;
        }
        if (order.timestamp) {
          this.form.productDetails.timestamp = moment(order.timestamp).format("D.M.YYYY");
        }
        this.form.productDetails.products = [];
        order.rows
          .filter(
            (row) =>
              row.product.id !== HandlingFee.sku && row.product.id !== SurfaceTreatmentFee.sku,
          )
          .filter(
            (row) => !this.$store.state.productData.serviceproducts.ids.includes(row.product.id),
          )
          .forEach((row) => {
            this.form.productDetails.products.push({
              id: row.product.id,
              name: row.product.name,
              orderCount: row.quantity,
              imageUrl: row.product.imageUrl,
              returnCount: 0,
            });
          });
        this.form.productDetails.productCount = this.form.productDetails.products.length;
      }
    },
  },
});
</script>
